import { useOnChange } from '@framework/form/hooks/use-on-change/useOnChange.ts';
import { useFormSetValue } from '@framework/form/hooks/useFormSetValue.ts';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { Space } from 'antd';
import { type SizeType } from 'antd/es/config-provider/SizeContext';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import styled from 'styled-components';
import {
  isObjectsArrayField,
  isSameFieldDatatype,
} from '../../../datatype/Datatype.ts';
import { type IReference } from '../../../references/IReference.ts';
import { ExpressionReferenceInput } from '../../../references/input/ExpressionReferenceInput.tsx';
import { type IConditionOperatorArgument } from '../../operators/IConditionOperator.ts';
import { ConditionOperatorInput } from '../../operators/input/ConditionOperatorInput.tsx';
import { OperatorsRepository } from '../../operators/OperatorsRepository.ts';
import { type IConditionExpression } from '../IConditionExpression.ts';
import { ConditionArgumentInput } from './ConditionArgumentInput.tsx';

const StyeledFormItem = styled(FormItem)`
  &&& {
    .ant-form-item-control-input-content {
      display: flex;
      flex-direction: column;
    }
  }
`;

export interface IConditionInputProps {
  references: IReference[];
  name: string;
  size?: SizeType;
}

/**
 * Indica se un argomento è inline (quindi interno, nel rendering, allo `Space.Compact`)
 * o no (quindi esterno, es. va a capo).
 */
const isInlineArgument = (arg: IConditionOperatorArgument) =>
  arg.type !== 'arrayMatching';

const isRowArgument = (
  arg: IConditionOperatorArgument,
  condition: IConditionExpression,
) =>
  arg.type === 'arrayMatching' &&
  isObjectsArrayField(condition.reference.field);

/**
 * Permette di definire una condizione su un riferimento ad un'altro widget.
 */
export function ConditionInput(props: IConditionInputProps) {
  const { name, references, size } = props;

  const setValue = useFormSetValue();
  const condition: IConditionExpression | null = useWatch({
    name: props.name,
    defaultValue: { reference: null }, // TODO Verificare se ne abbiamo effetivamente bisogno
  });

  /**
   * Nel selettore mostriamo solo le reference per cui esiste almeno un
   * operatore di condizione applicabile: tipi senza operatori (es. `Map`,
   * `Object`, `Empty`) darebbero una condizione incompletabile. È filtro per
   * disponibilità operatore, non un elenco hardcoded di datatype.
   */
  const selectableReferences = useMemo(
    () =>
      references.filter(
        reference => OperatorsRepository.forField(reference.field).length > 0,
      ),
    [references],
  );

  const operator = useMemo(
    () => OperatorsRepository.get(condition?.operator),
    [condition?.operator],
  );
  const argsIndexed = useMemo(
    () => operator?.args.map((arg, i) => ({ arg, index: i })) ?? [],
    [operator?.args],
  );

  /**
   * Se cambia il riferimento, azzera l'operatore e gli argomenti.
   */
  useOnChange(condition?.reference?.id, (prevId, nextId) => {
    if (!prevId) return;

    const prevReference = references.find(({ id }) => id === prevId);
    const nextReference = references.find(({ id }) => id === nextId);

    // Evitiamo possibili errori di coerenza fra i dati
    if (!prevReference || !nextReference) return;

    if (!isSameFieldDatatype(prevReference?.field, nextReference?.field)) {
      setValue(`${name}.operator`, null);
      setValue(`${name}.arguments`, []);
    }
  });

  return (
    <StyeledFormItem
      name={name}
      hasFeedback={false}
      layout={{ removeHelpMargin: true }}
      // Forziamo il campo ad essere considerato "touched"
      // in modo da mostrare gli errori di validazione nel momento
      // in cui si prova a sottomettere il form (tasto Applica)
      // anche senza aver interagito con il campo.
      isAlwaysTouched
    >
      <Space.Compact size={size ?? 'middle'}>
        <ExpressionReferenceInput
          size={size ?? 'middle'}
          name={`${name}.reference`}
          references={selectableReferences}
        />
        {condition?.reference && (
          <ConditionOperatorInput
            name={`${name}.operator`}
            field={condition.reference.field}
          />
        )}
        {condition &&
          argsIndexed
            .filter(({ arg }) => !isRowArgument(arg, condition))
            .map(({ arg, index }) => (
              <ConditionArgumentInput
                key={index}
                size={size ?? 'middle'}
                name={`${name}.arguments.${index}`}
                condition={condition}
                arg={arg}
                references={references}
              />
            ))}
      </Space.Compact>
      {condition &&
        argsIndexed
          .filter(({ arg }) => isRowArgument(arg, condition))
          .map(({ arg, index }) => (
            <div key={index}>
              <ConditionArgumentInput
                key={index}
                size={size ?? 'middle'}
                name={`${name}.arguments.${index}`}
                condition={condition}
                arg={arg}
                references={references}
              />
            </div>
          ))}
    </StyeledFormItem>
  );
}
